"use client";

import { orderedVariants } from "./variant-presentation";
import { useWebshopT } from "./i18n";
import { variantForAttributeValue, variationAttributes, variationLabel } from "./product-variants";
import type { StorefrontProduct } from "./types";
import type { StorefrontFeatures } from "../features";

export function VariantSelector({ product, value, onChange, locale, disabled = false, mode = "select", swatchColors = {}, labels, initialVariant = "first", wholeVariants = false, disableUnavailable = false }: {
  product: StorefrontProduct; value?: string; onChange(id: string): void; locale?: string; disabled?: boolean;
  mode?: "select" | "swatches"; swatchColors?: Record<string, string>; labels?: StorefrontFeatures["labels"]; initialVariant?: "first" | "none";
  wholeVariants?: boolean; disableUnavailable?: boolean;
}) {
  const t = useWebshopT(locale, labels);
  const attributes = variationAttributes(product);
  const variants = orderedVariants(product);
  const selected = product.variants.find((variant) => variant.id === value);
  if (product.variants.length < 2 && !(initialVariant === "none" && product.productType === "variable")) return null;
  if (mode === "swatches" && wholeVariants) return <fieldset className="webshops-variant-selector" data-mode={mode} disabled={disabled}>
    <legend>{t("module.webshops.product.choose_variant", "Choose a variation")}</legend>
    <div className="webshops-variant-swatches">{variants.map((variant) => <button type="button" key={variant.id} aria-pressed={value === variant.id} disabled={disableUnavailable && !variant.inStock} onClick={() => onChange(variant.id)}>
      {Object.values(variant.optionValues).map((option) => /^#[a-f0-9]{6}$/i.test(swatchColors[option] ?? "") ? <span key={option} className="webshops-variant-swatch__color" aria-hidden="true" style={{ backgroundColor: swatchColors[option] }} /> : null)}
      {variationLabel(variant)}</button>)}</div>
  </fieldset>;
  return <fieldset className="webshops-variant-selector" data-mode={mode} disabled={disabled}>
    <legend>{t("module.webshops.product.choose_variant", "Choose a variation")}</legend>
    {attributes.length ? attributes.map((attribute) => {
      const name = attribute.canonicalName ?? attribute.name;
      if (mode === "swatches") return <div className="webshops-variant-selector__field" key={name} data-attribute={name}>
        <span>{attribute.name}</span><div className="webshops-variant-swatches" role="group" aria-label={attribute.name}>
          {attribute.values.map((option) => <button type="button" key={option} aria-pressed={selected?.optionValues[name] === option} onClick={() => {
            const next = variantForAttributeValue(product.variants, selected, name, option); if (next) onChange(next.id);
          }}>{/^#[a-f0-9]{6}$/i.test(swatchColors[option] ?? "") && <span className="webshops-variant-swatch__color" aria-hidden="true" style={{ backgroundColor: swatchColors[option] }} />}{attribute.valueLabels?.[option] ?? option}</button>)}
        </div>
      </div>;
      return <label className="webshops-variant-selector__field" key={name} data-attribute={name}>
        <span>{attribute.name}</span>
        <select value={selected?.optionValues[name] ?? ""} onChange={(event) => {
          const next = variantForAttributeValue(product.variants, selected, name, event.target.value);
          if (next) onChange(next.id);
        }}>
          <option value="" disabled>{t("module.webshops.product.choose_variant", "Choose a variation")}</option>
          {attribute.values.map((option) => <option key={option} value={option}>{attribute.valueLabels?.[option] ?? option}</option>)}
        </select>
      </label>;
    }) : <label className="webshops-variant-selector__field">
      <span>{t("module.webshops.product.choose_variant", "Choose a variation")}</span>
      <select value={value ?? ""} onChange={(event) => onChange(event.target.value)}>
        <option value="" disabled>{t("module.webshops.product.choose_variant", "Choose a variation")}</option>
        {variants.map((variant) => <option key={variant.id} value={variant.id} disabled={disableUnavailable && !variant.inStock}>{variationLabel(variant)}{!variant.inStock ? ` — ${t("module.webshops.product.out_of_stock", "Out of stock")}` : ""}</option>)}
      </select>
    </label>}
  </fieldset>;
}
